// Feed.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, orderBy, limit, getDocs,
  startAfter, doc, getDoc, updateDoc, setDoc, arrayUnion, arrayRemove, runTransaction
} from 'firebase/firestore';
import { db, auth } from './firebase';
import Navbar from './Navbar';
import { addDoc } from "firebase/firestore";
import { getInitials } from './utils';
import { FaHeart, FaRegHeart } from "react-icons/fa";
import SwipeWrapper from './SwipeWrapper';
import Modal from './Modal';
import CollaboratorsModal from './CollaboratorsModal';
import AddToWishlistModal from './AddToWishlistModal';
import LazyImage from './LazyImage';
import { sendLikeNotification, sendFollowNotification } from './notificationService';
import { getAffiliateLink, trackClick } from "./utils/affiliateUtils";
import { getPersonalizedFeed } from './utils/personalizedFeedAlgorithm';
import LikesManager from './utils/likesManager';
import { Capacitor } from '@capacitor/core';

// Heart animation CSS is handled via inline styles

export default function Feed() {
  const { t } = useTranslation();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [following, setFollowing] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from sessionStorage
    return sessionStorage.getItem('feedActiveTab') || "tutte";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [showModal, setShowModal] = useState(false);
  const [showAlert, setShowAlert] = useState("");
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [likeAnimations, setLikeAnimations] = useState({});
  const [expandedLists, setExpandedLists] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [userWishlists, setUserWishlists] = useState([]);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(null);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [algorithmInfo, setAlgorithmInfo] = useState(null);
  const loader = useRef(null);
  const containerRef = useRef();
  const navigate = useNavigate();

  const openModal = async (product, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setSelectedProduct(product);

    // Query per le wishlist di cui l'utente è proprietario
    const ownWishlistsQuery = query(
      collection(db, "wishlists"),
      where("userId", "==", currentUserId),
      where("archived", "==", false)
    );

    // Query per le wishlist dove l'utente è collaboratore
    const collaboratorWishlistsQuery = query(
      collection(db, "wishlists"),
      where("collaborators", "array-contains", currentUserId),
      where("archived", "==", false)
    );

    const [ownSnap, collaboratorSnap] = await Promise.all([
      getDocs(ownWishlistsQuery),
      getDocs(collaboratorWishlistsQuery)
    ]);

    const ownLists = ownSnap.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      isOwner: true 
    }));

    const collaboratorLists = collaboratorSnap.docs
      .filter(doc => {
        const data = doc.data();
        // 🔧 FIX: Exclude wishlists where user is the owner (prevents duplication)
        return data.userId !== currentUserId;
      })
      .map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        isOwner: false 
      }));

    // 🔧 FIX: Deduplicate wishlists by ID to prevent showing the same wishlist twice
    const merged = new Map();
    [...ownLists, ...collaboratorLists].forEach(w => {
      const prev = merged.get(w.id) || {};
      merged.set(w.id, { 
        ...prev, 
        ...w, 
        isOwner: prev.isOwner || w.isOwner 
      });
    });
    const allLists = Array.from(merged.values());
    setUserWishlists(allLists);
    setShowModal(true);
  };

  const addProductToWishlist = async (wishlistId) => {
    if (!selectedProduct) return;
    const ref = doc(db, "wishlists", wishlistId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    const updated = Array.isArray(data.products)
      ? [selectedProduct, ...data.products]
      : [selectedProduct];
    await updateDoc(ref, { products: updated });
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

const openUserPage = (userId) => {
    const currentScroll = window.scrollY;
    sessionStorage.setItem('fromScroll', currentScroll.toString());
    sessionStorage.setItem('fromPage', '/feed');
    sessionStorage.setItem('feedActiveTab', activeTab); // Preserve active tab
    navigate(`/utente/${userId}`, { 
      state: { 
        from: "/feed",
        feedTab: activeTab
      } 
    });
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      console.log("🔐 Auth state changed:");
      console.log("✅ user:", user);
      console.log("✅ currentUserId:", user?.uid);
      console.log("🔑 isAnonymous:", user?.isAnonymous);
      console.log("🔑 Auth token:", user?.accessToken ? "present" : "missing");
      setCurrentUserId(user ? user.uid : null);
    }, (error) => {
      console.error("❌ Auth error:", error);
    });
    return () => unsubscribe();
  }, []);

  // Ripristina la posizione di scroll quando si torna al Feed
  useEffect(() => {
    // Controlla sia sessionStorage che location.state per il ripristino
    const savedScrollPosition = sessionStorage.getItem('fromScroll') || location.state?.scrollY;
    const fromPage = sessionStorage.getItem('fromPage') || location.state?.fromPage;
    const fromBottomNav = location.state?.fromBottomNav;
    const shouldRestore = !fromBottomNav && (location.state?.shouldRestoreScroll || (savedScrollPosition && fromPage === '/feed'));

    console.log('🔄 Feed scroll restoration check:', { 
      savedScrollPosition, 
      fromPage, 
      shouldRestore,
      wishlistsLength: wishlists.length,
      locationState: location.state
    });

    if (shouldRestore && wishlists.length > 0) {
      // Aspetta che tutto sia renderizzato
      const timeoutId = setTimeout(() => {
        const position = parseInt(savedScrollPosition, 10);
        if (!isNaN(position)) {
          console.log('✅ Ripristinando scroll in Feed:', position);
          window.scrollTo({
            top: position,
            behavior: 'smooth'
          });

          // Pulisci dopo il ripristino
          sessionStorage.removeItem('fromScroll');
          sessionStorage.removeItem('fromPage');

          // Pulisci anche location.state se presente
          if (location.state?.shouldRestoreScroll) {
            navigate(location.pathname, { 
              state: { 
                ...location.state, 
                shouldRestoreScroll: false,
                scrollY: undefined 
              },
              replace: true // Use replace to avoid navigation history pollution
            });
          }
        }
      }, 800); // Increased delay to ensure images are loaded

      return () => clearTimeout(timeoutId);
    }
  }, [wishlists, location.state, navigate])

  useEffect(() => {
    if (!currentUserId) return;

    const fetchFollowing = async () => {
      try {
        console.log("Loading following list for user:", currentUserId?.slice(0,8));
        const snap = await getDoc(doc(db, "users", currentUserId));
        if (snap.exists()) {
          const data = snap.data();
          const followingList = Array.isArray(data.following) ? data.following : [];
          console.log("Seguiti caricati:", followingList, "| Count:", followingList.length);
          setFollowing(followingList);
        } else {
          console.log("User document not found, setting empty following list");
          setFollowing([]);
        }
      } catch (error) {
        console.error("Errore nel caricamento following:", error);
        setFollowing([]);
      }
    };

    fetchFollowing();
  }, [currentUserId]);
  const fetchWishlists = useCallback(async (isLoadMore = false) => {
    if (!currentUserId) return;

    try {
      console.info("FILTER APPLIED - Sort:", sortBy, "| Tab:", activeTab, "| User:", currentUserId?.slice(0,8));
      console.log("Starting feed fetch... Load More:", isLoadMore);
      console.log("User ID:", currentUserId);
      console.log("Sort by:", sortBy);
      console.log("Active tab:", activeTab);
      console.log("Following list:", following, "| Count:", following.length);
      // Always use personalized feed algorithm for unlimited social experience
      const feedResult = await getPersonalizedFeed(
        currentUserId,
        sortBy,
        activeTab,
        following,
        100, // Unlimited content - users can scroll for hours
        isLoadMore ? lastVisible : null // Cursor for pagination
      );

      console.log("Feed algorithm result:", {
        isPersonalized: feedResult.isPersonalized,
        algorithm: feedResult.algorithm,
        wishlistCount: feedResult.wishlists.length
      });

      // Process wishlists to add user data and like status
      const processedWishlists = await Promise.all(
        feedResult.wishlists.map(async (wishlist) => {
          const userSnap = await getDoc(doc(db, "users", wishlist.userId));
          const userData = userSnap.exists() ? userSnap.data() : {};
          
          return {
            ...wishlist,
            userName: userData.displayName || "Utente",
            avatar: userData.avatar || "",
            likedByMe: (wishlist.likes || []).includes(currentUserId),
            // Keep personalization metadata
            personalizedScore: wishlist.personalizedScore,
            recommendationReason: wishlist.recommendationReason
          };
        })
      );

      console.log("✅ Processed wishlists:", processedWishlists.length);
      
      // Set personalization state
      setIsPersonalized(feedResult.isPersonalized);
      setAlgorithmInfo(feedResult);
      
      // 🔧 FIX: Clear distinction between initial load and infinite scroll
      if (isLoadMore) {
        // Infinite scroll: append new wishlists (works for both personalized and standard)
        setWishlists(prev => {
          // 🔧 FIX: Deduplicate to prevent showing same wishlist multiple times
          const existingIds = new Set(prev.map(w => w.id));
          const newWishlists = processedWishlists.filter(w => !existingIds.has(w.id));
          return [...prev, ...newWishlists];
        });
      } else {
        // Initial load: replace all wishlists
        setWishlists(processedWishlists);
      }
      
      // Update pagination cursor and hasMore state
      if (feedResult.lastDoc) {
        setLastVisible(feedResult.lastDoc);
      }
      
      // Enable unlimited infinite scroll based on actual new content
      setHasMore(feedResult.hasMore && processedWishlists.length > 0);

    } catch (error) {
      console.error("❌ Error in fetchWishlists:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      
      // Fallback to standard feed if personalized fails
      await fetchStandardWishlists(isLoadMore);
    }
  }, [currentUserId, sortBy, activeTab, following, lastVisible]);

  // Fallback function for standard feed
  const fetchStandardWishlists = useCallback(async (isLoadMore = false) => {
    try {
      console.info("📊 STANDARD FEED FALLBACK - Sort:", sortBy, "| Tab:", activeTab, "| Following:", following.length);
      console.log("📊 Falling back to standard feed");
      
      // 🔧 FIX: Respect activeTab to prevent showing all wishlists in Following tab even on error
      if (activeTab === 'following') {
        if (following.length === 0) {
          console.log("⚠️ FALLBACK: Following tab but no followed users - returning empty");
          setWishlists([]);
          setHasMore(false);
          return;
        }
        console.log("✅ FALLBACK: Following tab with followed users - filtering by following list");
      } else {
        console.log("📋 FALLBACK: Explore tab - fetching all public wishlists");
      }

      // 🔧 SIMPLIFIED QUERY: Use same approach as main algorithm to avoid database index issues
      console.log('🔧 FALLBACK: Using simplified query to avoid database index requirements');
      let q = query(
        collection(db, "wishlists"),
        where("isPublic", "==", true),
        where("archived", "==", false),
        orderBy(sortBy === "recent" ? "createdAt" : "likesCount", "desc"),
        ...(lastVisible ? [startAfter(lastVisible)] : []),
        limit(100) // Unlimited content for endless scrolling
      );

      const snap = await getDocs(q);
      
      const newWishlists = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const safeData = {
            isPublic: data.isPublic ?? false,
            archived: data.archived ?? false,
            createdAt: data.createdAt ?? { seconds: Date.now() / 1000 },
            ...data
          };
          const userSnap = await getDoc(doc(db, "users", safeData.userId));
          const userData = userSnap.exists() ? userSnap.data() : {};
          const baseWishlist = {
            id: docSnap.id,
            ...safeData,
            userName: userData.displayName || "Utente",
            avatar: userData.avatar || ""
          };
          // Use LikesManager to normalize the wishlist data
          return LikesManager.normalizeWishlist(baseWishlist, currentUserId);
        })
      );

      console.log('📊 FALLBACK: Retrieved', newWishlists.length, 'total wishlists for tab:', activeTab);
      
      // 🔧 FILTER IN MEMORY for Following tab to avoid database index requirements  
      let filteredWishlists = newWishlists;
      if (activeTab === 'following' && following.length > 0) {
        const beforeFilter = filteredWishlists.length;
        filteredWishlists = filteredWishlists.filter(wishlist => following.includes(wishlist.userId));
        console.log('🎯 FALLBACK MEMORY FILTER APPLIED: Before:', beforeFilter, '| After:', filteredWishlists.length, '| Filtered by following list');
        console.log('📋 FALLBACK Following list used for filtering:', following);
        console.log('📋 FALLBACK Filtered wishlist userIds:', filteredWishlists.map(w => w.userId));
      }
      
      console.log('📊 FALLBACK Final filtered results:', filteredWishlists.length, 'wishlists for tab:', activeTab);

      // 🔧 FIX: Use same logic as main fetchWishlists
      if (isLoadMore) {
        // Infinite scroll: append new wishlists  
        setWishlists(prev => {
          // 🔧 FIX: Deduplicate to prevent showing same wishlist multiple times
          const existingIds = new Set(prev.map(w => w.id));
          const deduplicatedWishlists = filteredWishlists.filter(w => !existingIds.has(w.id));
          return [...prev, ...deduplicatedWishlists];
        });
      } else {
        // Initial load: replace all wishlists
        setWishlists(filteredWishlists);
      }
      
      setLastVisible(snap.docs[snap.docs.length - 1]);
      setHasMore(snap.docs.length > 0);
      setIsPersonalized(false);
      
    } catch (error) {
      console.error("❌ Error in standard feed fallback:", error);
    }
  }, [lastVisible, currentUserId, sortBy]);

  useEffect(() => {
    setWishlists([]);
    setLastVisible(null);
    setHasMore(true);
  }, [sortBy, searchTerm, activeTab]);

  // Trigger initial fetch when user/sort/tab changes
  useEffect(() => {
    if (currentUserId) {
      console.log("🚀 Triggering initial feed fetch...");
      fetchWishlists(false); // false = initial load
    }
  }, [currentUserId, sortBy, activeTab, following]);

  useEffect(() => {
    if (!loader.current || !hasMore) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchWishlists(true); // true = load more
    }, { threshold: 1 });
    observer.observe(loader.current);
    return () => observer.disconnect();
  }, [fetchWishlists, hasMore]);

  const toggleFollow = async (targetUserId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Controllo semplificato di autenticazione
    if (!currentUserId || !auth.currentUser) {
      console.log("❌ Utente non autenticato per follow");
      return;
    }

    // Verifica che l'utente non stia cercando di seguire se stesso
    if (currentUserId === targetUserId) {
      console.log("❌ Non puoi seguire te stesso");
      return;
    }

    const userRef = doc(db, "users", currentUserId);
    const targetRef = doc(db, "users", targetUserId);
    const isFollowing = following.includes(targetUserId);

    try {
      // Verifica che entrambi i documenti esistano prima di procedere
      const [userDoc, targetDoc] = await Promise.all([
        getDoc(userRef),
        getDoc(targetRef)
      ]);

      if (!userDoc.exists() || !targetDoc.exists()) {
        console.error("❌ Uno dei documenti utente non esiste");
        return;
      }

      if (isFollowing) {
        // Unfollow
        await updateDoc(userRef, { following: arrayRemove(targetUserId) });
        await updateDoc(targetRef, { followers: arrayRemove(currentUserId) });
        setFollowing(prev => prev.filter(id => id !== targetUserId));
        console.log("✅ Unfollow completato");
      } else {
        // Follow
        await updateDoc(userRef, { following: arrayUnion(targetUserId) });
        await updateDoc(targetRef, { followers: arrayUnion(currentUserId) });
        setFollowing(prev => [...prev, targetUserId]);
        console.log("✅ Follow completato");

        // Invia notifica
        try {
          console.log('[Notifications][Feed] Creo notifica follow da piattaforma:', Capacitor.getPlatform(), 'sender:', currentUserId, 'recipient:', targetUserId);
          await sendFollowNotification(currentUserId, t('common.user'), targetUserId);
          console.log("✅ [Feed] Notifica follow inviata con successo da piattaforma:", Capacitor.getPlatform());
        } catch (notifError) {
          console.error("❌ [Feed] ERRORE notifica follow:", notifError);
          console.error("❌ [Feed] Stack trace:", notifError.stack);
        }
      }
    } catch (error) {
      console.error("❌ Errore toggle follow:", error);
      console.error("❌ Dettagli errore:", {
        code: error.code,
        message: error.message,
        currentUserId,
        targetUserId,
        isFollowing
      });

      // Ripristina lo stato in caso di errore
      if (error.code === 'permission-denied') {
        console.error("❌ Permesso negato - controlla le regole Firestore");
      }
    }
  };
  const toggleLike = async (wishlistId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Controllo semplificato di autenticazione
    if (!currentUserId || !auth.currentUser) {
      console.log("❌ Utente non autenticato per like");
      return;
    }

    const currentWishlist = wishlists.find(w => w.id === wishlistId);
    if (!currentWishlist) {
      console.log("❌ Wishlist non trovata:", wishlistId);
      return;
    }

    const wasLiked = LikesManager.isLikedByUser(currentWishlist, currentUserId);

    try {
      // Use the unified likes manager to handle the toggle
      const updatedWishlist = await LikesManager.toggleLike(
        wishlistId, 
        currentUserId, 
        currentWishlist
      );

      // Update local state with the result
      setWishlists(prev => 
        LikesManager.updateWishlistsAfterLike(prev, wishlistId, updatedWishlist)
      );

      // Animazione
      setLikeAnimations(prev => ({ ...prev, [wishlistId]: true }));
      setTimeout(() => {
        setLikeAnimations(prev => ({ ...prev, [wishlistId]: false }));
      }, 300);

      // Invia notifica se stiamo mettendo like
      if (!wasLiked && currentWishlist.userId !== currentUserId) {
        try {
          console.log('[Notifications][Feed] Creo notifica like da piattaforma:', Capacitor.getPlatform(), 'sender:', currentUserId, 'recipient:', currentWishlist.userId, 'wishlist:', wishlistId);
          await sendLikeNotification(currentUserId, t('common.user'), currentWishlist.userId, wishlistId);
          console.log("✅ [Feed] Notifica like inviata con successo da piattaforma:", Capacitor.getPlatform());
        } catch (notifError) {
          console.error("❌ [Feed] ERRORE notifica like:", notifError);
          console.error("❌ [Feed] Stack trace:", notifError.stack);
        }
      }
    } catch (error) {
      console.error("❌ Errore toggle like:", error);
      console.error("❌ Dettagli errore:", {
        code: error.code,
        message: error.message,
        currentUserId,
        wishlistId,
        wasLiked
      });

      if (error.code === 'permission-denied') {
        console.error("❌ Permesso negato - controlla le regole Firestore");
      }
    }
  };

  const toggleExpand = (id) => {
    const wishlistElement = document.querySelector(`[data-wishlist-id="${id}"]`);
    const currentScrollY = window.scrollY;
    const elementTop = wishlistElement ? wishlistElement.offsetTop : 0;

    setExpandedLists(prev => {
      const wasExpanded = prev[id];

      // If we're collapsing, scroll back to the wishlist header
      if (wasExpanded) {
        setTimeout(() => {
          const headerOffset = 140; // Account for fixed header with tabs
          window.scrollTo({
            top: elementTop - headerOffset,
            behavior: 'smooth'
          });
        }, 50);
      }

      return { ...prev, [id]: !prev[id] };
    });
  };

  const filteredWishlists = wishlists.filter((wishlist) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // Ricerca per nome wishlist
    const nameMatch = wishlist.name?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(searchLower);

    // Ricerca per tag
    const tagMatch = wishlist.tags?.some(tag => 
      tag.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(searchLower)
    );

    // Ricerca per nome utente
    const userMatch = wishlist.userName?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(searchLower);

    return nameMatch || tagMatch || userMatch;
  });


  return (
    <SwipeWrapper redirectTo="/">
      <div style={{...styles.page, paddingBottom: "80px"}} ref={containerRef}>
        <Navbar />

      <div style={styles.headerFixed}>
        <div style={styles.tabs}>
          <div style={{ display: "flex", flex: 1, justifyContent: "center" }}>
            <button onClick={() => {
              setActiveTab("tutte");
              sessionStorage.setItem('feedActiveTab', "tutte");
            }} style={activeTab === "tutte" ? styles.activeTab : styles.tab}>
              {t('feed.explore')}
            </button>
            <button onClick={() => {
              setActiveTab("following");
              sessionStorage.setItem('feedActiveTab', "following");
            }} style={activeTab === "following" ? styles.activeTab : styles.tab}>
              {t('feed.following')}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaHeart 
              onClick={() => navigate('/liked')}
              style={{ 
                fontSize: "22px", 
                color: "#000", 
                cursor: "pointer",
                padding: "4px"
              }}
            />
          </div>
        </div>
        <div style={styles.searchSortRow}>
          <input
            type="text"
            placeholder={t('feed.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.search}
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.sort}
          >
            <option value="recent">{t('feed.sortRecent')}</option>
            <option value="popular">{t('feed.sortPopular')}</option>
          </select>
        </div>
      </div>

      <div style={styles.container}>
        {/* Empty state for following tab when user is not following anyone */}
        {activeTab === 'following' && following.length === 0 && filteredWishlists.length === 0 && (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyTitle}>{t('feed.notFollowingTitle')}</h3>
            <p style={styles.emptyMessage}>{t('feed.notFollowingMessage')}</p>
            <button 
              style={styles.exploreButton}
              onClick={() => {
                setActiveTab('tutte');
                sessionStorage.setItem('feedActiveTab', 'tutte');
              }}
            >
              {t('feed.exploreWishlists')}
            </button>
          </div>
        )}
        
        {/* Empty state for search with no results */}
        {activeTab !== 'following' && filteredWishlists.length === 0 && searchTerm && (
          <div style={styles.emptyState}>
            <h3 style={styles.emptyTitle}>{t('feed.noSearchResults')}</h3>
            <p style={styles.emptyMessage}>{t('feed.tryDifferentSearch')}</p>
          </div>
        )}

        {filteredWishlists.map(w => {
          const expanded = expandedLists[w.id];
          const visibleProducts = expanded ? w.products : (w.products || []).slice(0, 3);
          return (
            <div
              key={w.id}
              data-wishlist-id={w.id}
              style={{...styles.card,}}
              onClick={() => {
                sessionStorage.setItem('fromScroll', window.scrollY.toString());
                sessionStorage.setItem('fromPage', '/feed');
                navigate(`/wishlist/${w.id}`, { state: { from: "/feed" } });
              }}
            >
              <div style={styles.header}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                   onClick={(e) => {
                       e.stopPropagation();
                       const currentScrollY = window.scrollY;
                       sessionStorage.setItem('fromScroll', currentScrollY.toString());
                       sessionStorage.setItem('fromPage', '/feed');
                       sessionStorage.setItem('feedActiveTab', activeTab);
                       navigate(`/utente/${w.userId}`, { 
                         state: { 
                           from: "/feed",
                           feedTab: activeTab
                         } 
                       });
                     }}
                >
                  {w.avatar ? (
                    <LazyImage 
                      src={w.avatar} 
                      alt="avatar" 
                      style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} 
                    />
                  ) : (
                    <div style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "#000",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: "bold"
                    }}>
                      {getInitials(w.userName)}
                    </div>
                  )}
                <div
                    style={styles.username}
                    onClick={(e) => {
                       e.stopPropagation();
                       const currentScrollY = window.scrollY;
                       sessionStorage.setItem('fromScroll', currentScrollY.toString());
                       sessionStorage.setItem('fromPage', '/feed');
                       sessionStorage.setItem('feedActiveTab', activeTab);
                       navigate(`/utente/${w.userId}`, { 
                         state: { 
                           from: "/feed",
                           feedTab: activeTab
                         } 
                       });
                     }}
                  >
                    {w.userName}
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {w.userId !== currentUserId && (
                    <button
                      style={{
                        ...styles.followBtn,
                        opacity: !currentUserId ? 0.5 : 1,
                        cursor: !currentUserId ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      disabled={!currentUserId} // Disattiva il bottone se l'utente non è ancora loggato
                      onClick={(e) => toggleFollow(w.userId, e)}
                    >
                      {following.includes(w.userId) ? t('feed.unfollow') : t('feed.follow')}
                    </button>
                  )}
                  <span
                    style={{
                      ...styles.likeIcon,
                      transform: likeAnimations[w.id] ? "scale(1.3)" : "scale(1)",
                      transition: "transform 0.2s ease, opacity 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      opacity: !currentUserId ? 0.5 : 1,
                      cursor: !currentUserId ? 'not-allowed' : 'pointer',
                      userSelect: 'none'
                    }}
                    onClick={(e) => {
                      if (!currentUserId) return;
                      toggleLike(w.id, e);
                    }}
                  >
                    {LikesManager.isLikedByUser(w, currentUserId) ? (
                      <FaHeart style={{ color: "#000", transition: "color 0.2s ease" }} />
                    ) : (
                      <FaRegHeart style={{ color: "#000", transition: "color 0.2s ease" }} />
                    )}
                    {LikesManager.getLikesCount(w)}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "8px", position: "relative" }}>
                <h3 style={styles.title}>{w.name}</h3>
                {(w.isShared || (w.collaborators && w.collaborators.length > 0)) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCollaboratorsModal(w.id);
                    }}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      border: "2px solid #000",
                      borderRadius: "50%",
                      backgroundColor: "transparent"
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                )}
              </div>

              <div style={styles.tags}>
                {(w.tags || []).map((tag, i) => (
                  <span key={i} style={styles.homeTag}>#{tag}</span>
                ))}
              </div>

              <div style={styles.products}>
                {visibleProducts.map((p, i) => (
                  <div key={i} style={styles.product}>
                    {/* Render image or placeholder */}
                    <div style={styles.productImageContainer}>
                      {p.image ? (
                        <LazyImage
                          src={p.image}
                          alt={p.title}
                          style={styles.productImage}
                          loading="lazy"
                          onClick={() => {
                            sessionStorage.setItem('fromScroll', window.scrollY.toString());
                            sessionStorage.setItem('fromPage', '/feed');
                            navigate(`/product/${p.id}`, { state: { from: "/feed", wishlistId: w.id } });
                          }}
                        />
                      ) : (
                        <div style={styles.noImagePlaceholder}>
                          <span style={styles.noImageText}>{t('images.noImage')}</span>
                        </div>
                      )}
                    </div>
                    <a
                      href={getAffiliateLink({...p, wishlistId: w.id})}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click event
                        trackClick(p.id, w.id, 'amazon');
                      }}
                      style={{
                        ...styles.link,
                        wordBreak: "break-all",
                        overflowWrap: "break-word",
                        hyphens: "auto",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: "1.3"
                      }}
                    >
                      {p.title}
                    </a>
                    <button
                      style={styles.addBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(p, e);
                      }}
                    >
                      ＋
                    </button>
                  </div>
                ))}
                {w.products?.length > 3 && (
                  <button
                    style={styles.showMore}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(w.id);
                    }}
                  >
                    {expanded ? t('wishlist.showLess') : `+ ${w.products.length - 3} ${t('wishlist.moreProducts')}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {hasMore && <div ref={loader} style={{ height: 60 }} />}
      </div>

      <AddToWishlistModal
        showModal={showModal}
        onClose={closeModal}
        userWishlists={userWishlists}
        onAddToWishlist={addProductToWishlist}
      />

      {showCollaboratorsModal && (
        <CollaboratorsModal
          wishlistId={showCollaboratorsModal}
          wishlist={wishlists.find(w => w.id === showCollaboratorsModal)}
          currentUserId={currentUserId}
          onClose={() => setShowCollaboratorsModal(null)}
          viewOnly={wishlists.find(w => w.id === showCollaboratorsModal)?.userId !== currentUserId}
        />
      )}
      </div>
    </SwipeWrapper>
  );
  }
const styles = {
  page: {
    fontFamily: "Poppins, sans-serif",
    background: "#fff",
    color: "#000",
    minHeight: "100vh"
  },
  headerFixed: {
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 999,
    padding: "12px 20px",
    borderBottom: "1px solid #ddd"
  },
  container: {
    padding: "20px",
    paddingTop: "10px"
  },
  tabs: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px"
  },
  tab: {
    padding: "10px 30px",
    background: "none",
    border: "none",
    fontSize: "18px",
    fontWeight: "600",
    color: "#888",
    cursor: "pointer",
    position: "relative",
    borderBottom: "3px solid transparent",
    transition: "all 0.2s ease",
    flex: 1,
    textAlign: "center"
  },
  activeTab: {
    padding: "10px 30px",
    background: "none",
    border: "none",
    fontSize: "18px",
    fontWeight: "700",
    color: "#000",
    cursor: "pointer",
    position: "relative",
    borderBottom: "3px solid #000",
    transition: "all 0.2s ease",
    flex: 1,
    textAlign: "center"
  },
  heartIcon: {
    fontSize: "20px", 
    color: "#000", 
    cursor: "pointer",
    transition: "transform 0.2s ease"
  },
  searchSortRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: "10px",
    marginTop: "10px"
  },
  search: {
    flex: 1,
    height: "36px",
    padding: "0 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "13px",
    minWidth: "200px"
  },
  sort: {
    fontSize: "13px",
    height: "36px",
    padding: "0 10px",
    borderRadius: "6px",
    border: "1px solid #aaa",
    background: "#fff",
    color: "#000",
    appearance: "none"
  },
  card: {
    background: "#fafafa",
    border: "1px solid #ddd",
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
  },
  sharedCard: {
    background: "#4a4a4a",
    color: "#fff",
    border: "1px solid #666",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px"
  },
  username: {
    fontWeight: "600",
    cursor: "pointer",
    textDecoration: "underline"
  },
  followBtn: {
    fontSize: "13px",
    padding: "4px 10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  likeIcon: {
    cursor: "pointer",
    fontSize: "18px",
    color: "#000 !important"
  },
  title: {
    marginBottom: "10px",
    fontSize: "16px",
    fontWeight: "600",
    margin: 0
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "10px"
  },
  homeTag: {
    fontSize: "12px",
    background: "#eee",
    padding: "4px 10px",
    borderRadius: "20px",
    color: "#333"
  },
  products: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  product: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "8px"
  },
  productImageContainer: {
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  productImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "8px",
    cursor: "pointer",
  },
  noImagePlaceholder: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #eee",
  },
  noImageText: {
    fontSize: "12px",
    color: "#999",
    textAlign: "center",
  },
  link: {
    textDecoration: "none",
    color: "#000",
    fontSize: "14px",
    fontWeight: "500",
    flex: 1,
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%"
  },
  addBtn: {
    fontSize: "18px",
    padding: "4px 10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  },
  showMore: {
    background: "none",
    border: "none",
    color: "#000",
    fontSize: "13px",
    cursor: "pointer",
    marginTop: "5px",
    textAlign: "left",
    padding: 0
  },
  modalContent: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "320px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
    textAlign: "center",
    maxHeight: "80vh",
    overflowY: "auto",
    position: "relative",
    margin: "auto"
  },
  modalBtn: {
    width: "100%",
    padding: "10px",
    marginBottom: "10px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer"
  },
  modalCancel: {
    width: "100%",
    padding: "10px",
    background: "#e0e0e0",
    color: "#000",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer"
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#666"
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "20px"
  },
  emptyTitle: {
    fontSize: "20px",
    fontWeight: "600",
    margin: "0 0 10px 0",
    color: "#333"
  },
  emptyMessage: {
    fontSize: "16px",
    lineHeight: "1.5",
    margin: "0 0 30px 0"
  },
  exploreButton: {
    fontSize: "16px",
    fontWeight: "600",
    padding: "12px 24px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease"
  }
};